import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import { useI18n } from '../../i18n';

interface EnergyByVehicle {
  vehicleName: string;
  totalEnergy: number;
  transactionCount?: number;
}

interface EnergyByVehicleChartProps {
  data: EnergyByVehicle[];
  loading?: boolean;
}

function EnergyByVehicleChart({ data, loading }: EnergyByVehicleChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-80 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Check if we have energy data or only transaction counts
  const hasEnergyData = data.some(d => d.totalEnergy > 0);

  // Create pie data with colors
  const colors = [
    '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0',
    '#047857', '#065f46', '#064e3b', '#022c22', '#14532d'
  ];

  const pieData = data.map((d, index) => ({
    name: d.vehicleName || t('eq_unknown'),
    y: hasEnergyData ? d.totalEnergy : (d.transactionCount || 0),
    color: colors[index % colors.length],
  }));

  const chartTitle = hasEnergyData ? t('leg2_vendor_consumption_kwh') : t('leg2_vendor_transactions');
  const chartSubtitle = hasEnergyData 
    ? t('leg2_vendor_energy_split')
    : t('leg2_vendor_transactions_split');
  const tooltipFormat = hasEnergyData
    ? `{series.name}: <b>{point.percentage:.1f}%</b><br/>${t('energy')}: <b>{point.y:.2f} kWh</b>`
    : `{series.name}: <b>{point.percentage:.1f}%</b><br/>${t('leg2_transactions')}: <b>{point.y}</b>`;

  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      height: 300,
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: chartTitle,
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: chartSubtitle,
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    tooltip: {
      pointFormat: tooltipFormat,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#10b981',
      borderRadius: 8,
    },
    accessibility: {
      point: {
        valueSuffix: '%',
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.percentage:.1f}%',
          style: {
            fontSize: '11px',
            color: '#374151',
            textOutline: 'none',
          },
        },
        showInLegend: true,
      },
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        fontSize: '11px',
        color: '#374151',
      },
    },
    series: [{
      type: 'pie',
      name: t('leg2_share'),
      data: pieData,
    }],
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
      {data.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-4">
          {t('leg2_no_data_available')}
        </div>
      )}
    </div>
  );
}

export default EnergyByVehicleChart;
